import * as fs from 'fs';
import * as net from 'net';
import * as path from 'path';

export type AgentFramework = 'nextjs' | 'vite' | 'cra' | 'unknown';

const SDK_PACKAGE_NAMES = ['@explorer02/cfm-survey-sdk', '@repo/sdk'] as const;

function readPortFromEnv(): number | undefined {
  const raw = process.env.PORT?.trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function readViteDevPort(projectRoot: string): number {
  const candidates = [
    path.join(projectRoot, 'vite.config.ts'),
    path.join(projectRoot, 'vite.config.js'),
    path.join(projectRoot, 'vite.config.mjs'),
  ];
  for (const filePath of candidates) {
    if (!fs.existsSync(filePath)) continue;
    const source = fs.readFileSync(filePath, 'utf8');
    const match = source.match(/server\s*:\s*\{[^}]*port\s*:\s*(\d+)/s);
    if (match) return Number(match[1]);
  }
  return 5173;
}

const DEFAULT_PROBE_PORTS = [3000, 5173, 5174, 3001] as const;

/** Ordered candidate ports for live dev-server probe (deduped). */
export function buildSurveyDevPortCandidates(projectRoot: string): number[] {
  const ports: number[] = [];
  const portFromEnv = readPortFromEnv();
  if (portFromEnv) ports.push(portFromEnv);

  const pkgPath = path.join(projectRoot, 'package.json');
  if (fs.existsSync(pkgPath)) {
    const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as {
      dependencies?: Record<string, string>;
      devDependencies?: Record<string, string>;
    };
    const framework = detectFramework(pkg);
    if (framework === 'vite') {
      ports.push(readViteDevPort(projectRoot));
    } else if (framework === 'nextjs' || framework === 'cra') {
      ports.push(3000);
    }
  }

  ports.push(...DEFAULT_PROBE_PORTS);
  return [...new Set(ports)];
}

export type PortProbeFn = (port: number) => Promise<boolean>;

/** Returns true when something accepts TCP on localhost:{port}. */
export function probeLocalPort(port: number, timeoutMs = 800): Promise<boolean> {
  return new Promise((resolve) => {
    const socket = net.connect({ port, host: '127.0.0.1' });
    const onDone = (alive: boolean) => {
      socket.removeAllListeners();
      socket.destroy();
      resolve(alive);
    };
    socket.setTimeout(timeoutMs);
    socket.once('connect', () => onDone(true));
    socket.once('timeout', () => onDone(false));
    socket.once('error', () => onDone(false));
  });
}

export interface ResolveLiveSurveyDevUrlResult {
  url: string;
  probed: boolean;
  warning?: string;
}

/** Probe candidate ports; fall back to config/default URL with warning when none respond. */
export async function resolveLiveSurveyDevUrl(
  projectRoot: string,
  options?: { probe?: PortProbeFn },
): Promise<ResolveLiveSurveyDevUrlResult> {
  const probe = options?.probe ?? probeLocalPort;
  const candidates = buildSurveyDevPortCandidates(projectRoot);

  for (const port of candidates) {
    if (await probe(port)) {
      return { url: `http://localhost:${port}`, probed: true };
    }
  }

  const fallback = resolveSurveyDevUrl(projectRoot);
  return {
    url: fallback,
    probed: false,
    warning:
      'No listening dev server found on candidate ports; using config/default URL. Start `npm run dev` and re-probe before sharing preview.',
  };
}

/** Default local survey app URL for client handoff after INPUT 2 / Phase 6c. */
export function resolveSurveyDevUrl(projectRoot: string): string {
  const pkgPath = path.join(projectRoot, 'package.json');
  let framework: AgentFramework = 'unknown';
  if (fs.existsSync(pkgPath)) {
    const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as {
      dependencies?: Record<string, string>;
      devDependencies?: Record<string, string>;
    };
    framework = detectFramework(pkg);
  }

  const portFromEnv = readPortFromEnv();
  if (portFromEnv) return `http://localhost:${portFromEnv}`;

  switch (framework) {
    case 'vite':
      return `http://localhost:${readViteDevPort(projectRoot)}`;
    case 'nextjs':
    case 'cra':
      return 'http://localhost:3000';
    default:
      return 'http://localhost:3000';
  }
}

export function detectFramework(packageJson: {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}): AgentFramework {
  const deps = {
    ...packageJson.dependencies,
    ...packageJson.devDependencies,
  };

  if (deps.next) return 'nextjs';
  if (deps.vite) return 'vite';
  if (deps['react-scripts']) return 'cra';
  return 'unknown';
}

export function resolveSdkPackageName(packageJson: {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}): string | undefined {
  const deps = {
    ...packageJson.dependencies,
    ...packageJson.devDependencies,
  };

  return SDK_PACKAGE_NAMES.find((name) => Boolean(deps[name]));
}

export function isAwsDeployTarget(surveyUiConfig: { deploy?: { target?: string } }): boolean {
  return surveyUiConfig.deploy?.target === 'aws';
}

export function nextConfigIncludesTranspilePackages(
  nextConfigSource: string,
  sdkPackageName: string,
): boolean {
  return (
    nextConfigSource.includes('transpilePackages') &&
    nextConfigSource.includes(sdkPackageName)
  );
}

export function nextConfigHasStaticExport(nextConfigSource: string): boolean {
  return (
    /output\s*:\s*['"]export['"]/.test(nextConfigSource) &&
    /assetPrefix\s*:\s*['"]\.\/['"]/.test(nextConfigSource)
  );
}

export function viteConfigHasRelativeBase(viteConfigSource: string): boolean {
  return /base\s*:\s*['"]\.\/['"]/.test(viteConfigSource);
}

export function sourceHasNextImports(source: string): boolean {
  return /from\s+['"]next\//.test(source);
}

export function sourceHasUseClientDirective(source: string): boolean {
  return /^['"]use client['"];?\s*$/m.test(source);
}
